// Discover (named Suggestions until 29 Jul 2026): everything the people
// you follow have published, partitioned by your decisions into five tabs,
// New / Active / Later / Passed / Hidden. Nothing here is "suggested"; the
// waiting lane is called New, never Waiting. Every new practice offers
// three equal answers; the Hidden tab exists because filters must never
// work silently. Search reaches the whole catalogue behind an explicit
// scope toggle (spec section 5, "Search on a surface").
import type { Descriptor } from "../shell/descriptor.js";
import { Avatar } from "../shell/Avatar.js";
import { DiscoverHero } from "../shell/DiscoverHero.js";
import { PagedList, SugCard, useExpert } from "../shell/fragments.js";
import { ExpertSelect, scopeTabs, SearchBox, useDebouncedSearch } from "../shell/SurfaceSearch.js";
import { useStore, type AppStore, type PracticeSearchHit } from "../state/store.js";

function prettyDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function LaterRow({ cardId, until, reason }: { cardId: string; until: string; reason: string | null }) {
  const s = useStore();
  const card = s.appState?.cards[cardId];
  if (!card) return null;
  // The payload carries every plan item, stopped included, so a practice
  // the user once ran reads "Start again" while a never-started one reads
  // "Start now" (Rob, 29 Jul 2026).
  const started = s.appState?.plan.some((p) => p.cardId === cardId) ?? false;
  return (
    <div className="lrow">
      <span className="grow">
        <button style={{ display: "block" }} onClick={() => s.openSheet({ kind: "practice", cardId })}>
          <strong>{card.do}</strong>
        </button>
        <small>
          Back on {prettyDate(until)}
          {reason ? ` · you said: ${reason}` : ""}
        </small>
      </span>
      <button className="btn" onClick={() => void s.unsnooze(cardId)}>
        {started ? "Start again" : "Start now"}
      </button>
    </div>
  );
}

// A paused practice of your own: same shape as LaterRow, but the return
// runs through the plan (status paused + date), not the snooze table.
export function PausedRow({ cardId, planItemId, until }: { cardId: string; planItemId: string; until: string | null }) {
  const s = useStore();
  const card = s.appState?.cards[cardId];
  if (!card) return null;
  return (
    <div className="lrow">
      <span className="grow">
        <button style={{ display: "block" }} onClick={() => s.openSheet({ kind: "practice", cardId })}>
          <strong>{card.do}</strong>
        </button>
        <small>{until ? `Taking a break · back on ${prettyDate(until)}` : "Taking a break"}</small>
      </span>
      {/* A pause always follows a run, so this one is never "Start now". */}
      <button className="btn" onClick={() => void s.setPlanStatus(planItemId, "active")}>
        Start again
      </button>
    </div>
  );
}

// One decision, one row (28 Jul 2026): an expert- or topic-scope no
// thanks covers many practices, and a row per practice read as a wall of
// passes the user never individually made.
export function groupPassed(
  passed: Array<{ cardId: string; scope: string; declineId: string; reason: string | null }>,
): Array<{ declineId: string; scope: string; reason: string | null; cardIds: string[] }> {
  const groups = new Map<string, { declineId: string; scope: string; reason: string | null; cardIds: string[] }>();
  for (const x of passed) {
    const g = groups.get(x.declineId) ?? { declineId: x.declineId, scope: x.scope, reason: x.reason, cardIds: [] };
    g.cardIds.push(x.cardId);
    groups.set(x.declineId, g);
  }
  return [...groups.values()];
}

export function PassedRow({ group }: { group: ReturnType<typeof groupPassed>[number] }) {
  const s = useStore();
  const card = s.appState?.cards[group.cardIds[0] ?? ""];
  const lite = useExpert(card?.expertId ?? "");
  if (!card) return null;
  const topic = s.appState?.topics.find((t) => t.id === card.topicId);
  const who = lite.short ?? lite.name;
  const label =
    group.scope === "expert"
      ? `Everything from ${who}`
      : group.scope === "topic"
        ? `Everything under ${topic?.label ?? "a topic"}`
        : card.do;
  const sub =
    group.scope === "card"
      ? `${who} · ${group.reason ?? "Passed"}`
      : `${group.cardIds.length} practice${group.cardIds.length === 1 ? "" : "s"} · one undo brings them all back`;
  return (
    <div className="dash" style={{ display: "flex", alignItems: "center", gap: 12 }}>
      {group.scope !== "topic" && <Avatar e={lite} size="s" />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>{label}</strong>
        <small>{sub}</small>
      </span>
      <button className="btn quiet" onClick={() => void s.undecline(group.declineId)}>
        Undo
      </button>
    </div>
  );
}

function HiddenRow({ cardId, by, switchRef }: { cardId: string; by: string; switchRef: string }) {
  const s = useStore();
  const card = s.appState?.cards[cardId];
  const lite = useExpert(card?.expertId ?? "");
  if (!card) return null;
  const topic = s.appState?.topics.find((t) => t.id === card.topicId);
  let why: string;
  let act: React.ReactNode;
  if (by === "expert-mute") {
    const [expertId, topicId] = switchRef.split(":");
    const e = s.appState?.experts[expertId ?? ""];
    why = `${topic?.label ?? "This topic"} is muted from ${e?.short ?? e?.name ?? "them"}`;
    act = (
      <button className="btn quiet" onClick={() => void s.setMute(expertId ?? "", topicId ?? "", false)}>
        Unmute
      </button>
    );
  } else {
    why = `${topic?.label ?? "This topic"} is off`;
    act = (
      <button className="btn" onClick={() => void s.setTopic(switchRef, true)}>
        Turn on
      </button>
    );
  }
  return (
    <div className="dash" style={{ display: "flex", alignItems: "center", gap: 12 }}>
      <Avatar e={lite} size="s" />
      <span style={{ flex: 1, minWidth: 0 }}>
        <strong>{card.do}</strong>
        <small>
          {lite.short ?? lite.name} · {why}
        </small>
      </span>
      {act}
    </div>
  );
}

// An added practice, lightly: the full card with the week strip lives on
// My Practice; two screens should not claim the same job.
function ActiveRow({ cardId, addedAt }: { cardId: string; addedAt: string }) {
  const s = useStore();
  const card = s.appState?.cards[cardId];
  const lite = useExpert(card?.expertId ?? "");
  if (!card) return null;
  return (
    <div className="lrow">
      <Avatar e={lite} size="s" />
      <span className="grow">
        <button style={{ display: "block" }} onClick={() => s.openSheet({ kind: "practice", cardId })}>
          <strong>{card.do}</strong>
        </button>
        <small>
          In your practice since {prettyDate(addedAt.slice(0, 10))} · {lite.short ?? lite.name}
        </small>
      </span>
    </div>
  );
}

function stateNote(hit: PracticeSearchHit): string | null {
  if (hit.state === "added") return "in your practice";
  if (hit.state === "later") return hit.until ? `back on ${prettyDate(hit.until)}` : "saved for later";
  if (hit.state === "passed") return "you passed on this";
  return hit.followed ? null : "outside your follows";
}

/* A result is the same card the New tab shows (2 Aug 2026, Rob: "just
   filtering the cards in the same view"), not a compact row of its own.
   Search used to swap the grid for a list, so finding a practice and being
   offered one looked like two different products, and the row could not carry
   the picture, the burden line or the three decisions.

   The one thing the row had that the card does not is the state note, because
   a search reaches across every lane: something already added, snoozed, or
   passed on can match. That takes the card's `why` slot, which is the line
   under the practice and exactly where a note about it belongs. The actions
   stay as they are on any card: each opens the practice modal or its
   popover, and the modal is the surface that knows what state the practice is
   actually in. */
function hitWhy(hit: PracticeSearchHit, name: string): string {
  const note = stateNote(hit);
  return note ? `${name} · ${note}` : name;
}

// Debounce lives in a mounted component so the screen stays a pure function.
function DiscoverSearchRunner() {
  const q = useStore((s) => s.discoverQ);
  useDebouncedSearch(q, () => void useStore.getState().runPracticeSearch());
  return null;
}

function SearchResults({ state }: { state: AppStore }) {
  const found = state.practiceSearch;
  const chosen = state.discoverExpert ? state.experts?.find((e) => e.id === state.discoverExpert) : undefined;
  const followed = chosen ? (state.appState?.follows.includes(chosen.id) ?? false) : true;
  // Full name here too (31 Jul 2026, Rob), so the line above the results
  // names the same person the results do.
  const who = chosen?.name ?? "";
  return (
    <>
      {chosen && !followed && (
        <div className="notice info">
          <span className="ni">◎</span>
          <span>
            You’re not following {who}. Their practices are open to look at either way and following
            them will include their content in your suggestions.
          </span>
        </div>
      )}
      {!found ? (
        <p className="empty">Searching…</p>
      ) : (
        <>
          {found.results.length ? (
            <PagedList className="grid2 plangrid" batch={12} key={`search:${state.discoverScope}:${state.discoverExpert ?? ""}`}>
              {found.results.map((hit) => {
                const card = found.cards[hit.cardId];
                const from = state.experts?.find((e) => e.id === card?.expertId)?.name ?? "";
                return card ? <SugCard key={hit.cardId} card={card} why={hitWhy(hit, from)} /> : null;
              })}
            </PagedList>
          ) : (
            <p className="empty">
              {chosen
                ? state.discoverQ.trim()
                  ? `Nothing from ${who} matches.`
                  : `Nothing from ${who} yet.`
                : state.discoverScope === "follows"
                  ? (
                      <>
                        Nothing from the people you follow matches.{" "}
                        <button
                          className="txt"
                          style={{ textDecoration: "underline" }}
                          onClick={() => state.setDiscoverScope("all")}
                        >
                          Search everyone.
                        </button>
                      </>
                    )
                  : "Nothing matches."}
            </p>
          )}
          {found.total > found.results.length && (
            <p className="empty" style={{ paddingBottom: 0 }}>
              Showing the first {found.results.length} of {found.total}. Add a word to narrow it.
            </p>
          )}
          {found.hidden > 0 && (
            <p className="empty" style={{ paddingBottom: 0 }}>
              {found.hidden} more match{found.hidden === 1 ? "es" : ""} but {found.hidden === 1 ? "is" : "are"} hidden
              by your switches.{" "}
              <button className="txt" style={{ textDecoration: "underline" }} onClick={() => state.nav("controls")}>
                All topic settings ›
              </button>
            </p>
          )}
        </>
      )}
    </>
  );
}

export function discover(state: AppStore, tabArg: string | null): Descriptor {
  const s = state.appState;
  const tab = tabArg && ["new", "active", "later", "passed", "hidden"].includes(tabArg) ? tabArg : state.discTab;
  if (!s) {
    return {
      nav: "discover",
      title: "Discover",
      main: <section className="panel glass"><p className="empty">Loading…</p></section>,
    };
  }

  // Naming a source narrows the lanes in place (31 Jul 2026, Rob): picking
  // someone from the toolbar used to swap the whole screen for a flat list
  // of results, which is a big move to make on a reader who only reached
  // for a filter. The search view is for queries now, and nothing else.
  const src = state.discoverExpert;
  const from = <T extends { cardId: string }>(rows: T[]): T[] =>
    src ? rows.filter((x) => s.cards[x.cardId]?.expertId === src) : rows;
  const lanes = {
    new: from(s.discover.new),
    active: from(s.discover.active),
    later: from(s.discover.later),
    hidden: from(s.discover.hidden),
  };
  const passedGroups = groupPassed(from(s.discover.passed));
  const counts = {
    new: lanes.new.length,
    active: lanes.active.length,
    later: lanes.later.length,
    // Decisions, not covered practices: one expert-wide pass counts once.
    passed: passedGroups.length,
    hidden: lanes.hidden.length,
  };
  // Their whole catalogue lives on their page, which is the honest place to
  // send someone whose lanes hold nothing from the person they just picked.
  const who = src ? (state.experts?.find((e) => e.id === src)?.name ?? "them") : "";
  const noneFrom = (
    <p className="empty">
      Nothing from {who} here.{" "}
      <button
        className="txt"
        style={{ textDecoration: "underline" }}
        onClick={() => src && state.nav("expert", src)}
      >
        Everything they publish ›
      </button>
    </p>
  );

  // The toolbar row: search plus the source select, above the filter bar,
  // same shape on every surface (29 Jul 2026).
  const searching = state.discoverQ.trim() !== "";
  const bar = (
    <div className="searchbar">
      <DiscoverSearchRunner />
      <SearchBox
        value={state.discoverQ}
        onChange={(q) => state.setDiscoverQ(q)}
        placeholder="Search practices…"
        label="Search practices"
      />
      {/* While searching the source becomes the strip's third pane (31 Jul
          2026, Rob), so it leaves the toolbar rather than appearing twice. */}
      {!searching && (
        <ExpertSelect
          value={state.discoverExpert}
          onChange={(id) => state.setDiscoverExpert(id)}
          kind="practices"
        />
      )}
    </div>
  );

  if (searching) {
    return {
      nav: "discover",
      title: "Discover",
      bar,
      tabs: scopeTabs(
        state.discoverScope,
        (sc) => {
          state.setDiscoverExpert(null);
          state.setDiscoverScope(sc);
        },
        { value: state.discoverExpert, onChange: (id) => state.setDiscoverExpert(id), kind: "practices" },
      ),
      main: <SearchResults state={state} />,
    };
  }

  let body: React.ReactNode;
  if (tab === "new") {
    body = counts.new ? (
      <PagedList className="grid2 plangrid" batch={12}>
        {lanes.new.map((x) => {
          const card = s.cards[x.cardId];
          return card ? <SugCard key={x.cardId} card={card} why={x.why} /> : null;
        })}
      </PagedList>
    ) : src ? (
      noneFrom
    ) : (
      <p className="empty">
        You’ve decided on everything so far. New practices arrive as the people you follow publish, not on a schedule.
      </p>
    );
  } else if (tab === "active") {
    body = counts.active ? (
      <>
        {lanes.active.map((x) => (
          <ActiveRow key={x.planItemId} cardId={x.cardId} addedAt={x.addedAt} />
        ))}
      </>
    ) : src ? (
      noneFrom
    ) : (
      <p className="empty">Nothing added yet. A practice you add lands here and on My Practice.</p>
    );
  } else if (tab === "later") {
    body = counts.later ? (
      <>{lanes.later.map((x) => <LaterRow key={x.snoozeId} {...x} />)}</>
    ) : src ? (
      noneFrom
    ) : (
      <p className="empty">Nothing saved for later.</p>
    );
  } else if (tab === "passed") {
    body = (
      <>
        {counts.passed ? (
          passedGroups.map((g) => <PassedRow key={g.declineId} group={g} />)
        ) : src ? (
          noneFrom
        ) : (
          <p className="empty">Nothing passed on.</p>
        )}
        <div className="notice safe" style={{ marginTop: 14 }}>
          <span className="ni">◇</span>
          <span>
            <b>No thanks is a real answer.</b> Nothing here counts against you, and one tap brings any of it back. There is no score.
          </span>
        </div>
      </>
    );
  } else {
    body = (
      <>
        {counts.hidden ? (
          lanes.hidden.map((x) => <HiddenRow key={x.cardId} {...x} />)
        ) : src ? (
          noneFrom
        ) : (
          <p className="empty">Your settings aren’t hiding anything.</p>
        )}
        <p className="empty" style={{ paddingBottom: 0 }}>
          Listed so a switch never works silently. Turning one on moves it to New. Nothing is blocked.{" "}
          <button className="txt" style={{ textDecoration: "underline" }} onClick={() => state.nav("controls")}>
            All topic settings ›
          </button>
        </p>
      </>
    );
  }

  // The hero rides above the toolbar on every tab, so switching tabs does not
  // make the top of the screen jump; it stands down while searching, where
  // the answer is the list and a picture is in the way. Two slides is the
  // floor: one practice is a still, not a slideshow.
  const heroCards = lanes.new.map((x) => s.cards[x.cardId]).filter((c) => c !== undefined);

  return {
    nav: "discover",
    title: "Discover",
    hero: heroCards.length >= 2 ? <DiscoverHero cards={heroCards} /> : undefined,
    bar,
    tabs: {
      items: [
        ["new", "New"],
        ["active", "Active"],
        ["later", "Later"],
        ["passed", "Passed"],
        ["hidden", "Hidden"],
      ],
      counts,
      active: tab,
      onPick: (t) => {
        state.setDiscTab(t);
        if (state.arg) state.nav("discover");
      },
    },
    main: body,
  };
}
